import React, { useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  CircularProgress,
  Alert,
  Grid,
  Card,
  CardContent,
  Divider
} from '@mui/material';
import { useDexService } from '../../../contexts/DexServiceContext';
import { ethers } from 'ethers';
import { LogLevel } from '../../../types/dex';

interface TokenInfoProps {
  tokenAddress: string;
}

/**
 * 代币信息组件
 * 允许用户查询任意ERC20代币的详细信息
 */
const TokenInfo: React.FC<TokenInfoProps> = ({ tokenAddress }) => {
  const { dexService, isInitialized, addLog } = useDexService();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tokenData, setTokenData] = useState<{
    name: string;
    symbol: string;
    decimals: number;
    totalSupply: string;
    owner?: string;
    transfersEnabled?: boolean;
    verified?: boolean;
  } | null>(null);

  // 查询代币信息
  const fetchTokenInfo = async () => {
    if (!isInitialized || !dexService) {
      setError('DexService未初始化，请先配置服务');
      addLog({
        level: LogLevel.ERROR,
        message: 'DexService未初始化，无法查询代币信息'
      });
      return;
    }

    if (!tokenAddress || !ethers.utils.isAddress(tokenAddress)) {
      setError('请输入有效的代币合约地址');
      addLog({
        level: LogLevel.WARNING,
        message: '代币地址无效，请检查输入'
      });
      return;
    }

    setLoading(true);
    setError(null);
    setTokenData(null);

    try {
      addLog({
        level: LogLevel.INFO,
        message: `开始查询代币信息: ${tokenAddress}`
      });

      // 获取基本代币信息
      const tokenInfo = await dexService.getTokenInfo(tokenAddress);
      
      // 构建详细代币数据
      const detailedTokenData = {
        name: tokenInfo.name,
        symbol: tokenInfo.symbol,
        decimals: tokenInfo.decimals,
        totalSupply: tokenInfo.totalSupply ? ethers.utils.formatUnits(tokenInfo.totalSupply, tokenInfo.decimals) : '0',
      };
      
      setTokenData(detailedTokenData);

      addLog({
        level: LogLevel.SUCCESS,
        message: `成功获取代币信息: ${tokenInfo.name} (${tokenInfo.symbol})`,
        data: {
          address: tokenAddress,
          name: tokenInfo.name,
          symbol: tokenInfo.symbol,
          decimals: tokenInfo.decimals,
          totalSupply: detailedTokenData.totalSupply
        }
      });
    } catch (err: any) {
      console.error('获取代币信息时出错:', err);
      const errorMessage = err.message || '未知错误';
      setError(`获取代币信息时出错: ${errorMessage}`);
      addLog({
        level: LogLevel.ERROR,
        message: `获取代币信息失败: ${errorMessage}`,
        data: { address: tokenAddress }
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 2, mb: 3 }}>
      <Typography variant="h6" gutterBottom>
        代币信息查询
      </Typography>
      
      <Box sx={{ mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={fetchTokenInfo}
              disabled={loading || !tokenAddress}
            >
              {loading ? <CircularProgress size={24} /> : '查询信息'}
            </Button>
          </Grid>
        </Grid>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {tokenData && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              基本信息
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  名称
                </Typography>
                <Typography variant="body1">
                  {tokenData.name}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  符号
                </Typography>
                <Typography variant="body1">
                  {tokenData.symbol}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  小数位
                </Typography>
                <Typography variant="body1">
                  {tokenData.decimals}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Typography variant="body2" color="text.secondary">
                  总供应量
                </Typography>
                <Typography variant="body1">
                  {tokenData.totalSupply}
                </Typography>
              </Grid>
            </Grid>

            {tokenData.owner && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="h6" gutterBottom>
                  高级信息
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="body2" color="text.secondary">
                      拥有者
                    </Typography>
                    <Typography variant="body1" sx={{ wordBreak: 'break-all' }}>
                      {tokenData.owner}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      转账功能
                    </Typography>
                    <Typography variant="body1">
                      {tokenData.transfersEnabled ? '已启用' : '已禁用'}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      合约验证
                    </Typography>
                    <Typography variant="body1">
                      {tokenData.verified ? '已验证' : '未验证'}
                    </Typography>
                  </Grid>
                </Grid>
              </>
            )}
          </CardContent>
        </Card>
      )}
      
      <Alert severity="info" sx={{ mt: 2 }}>
        提示：如需查询代币余额，请使用"钱包余额"功能。
      </Alert>
    </Paper>
  );
};

export default TokenInfo;
